import * as THREE from 'three';
import { getCarById, getTrackById } from '../content';
import type { GameSnapshot, VehicleState } from '../core/types';
import { createCarModel, disposeCarModel, type CarModel } from './carFactory';
import type { GameRenderBridge } from './contracts';
import { configureTrackLighting, createEnvironment, type EnvironmentHandle } from './environmentFactory';
import { SpeedEffects } from './speedEffects';
import { createTrackModel, type TrackModel } from './trackFactory';

type SceneMode = 'showcase' | 'race';

export class GameRenderer {
  readonly element: HTMLCanvasElement;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(63, 16 / 9, 0.08, 2600);
  private readonly timer = new THREE.Timer();
  private resizeObserver: ResizeObserver | null = null;
  private frame = 0;
  private frameUsesAnimationFrame = true;
  private mounted = false;
  private sceneMode: SceneMode | null = null;
  private sceneKey = '';
  private car: CarModel | null = null;
  private track: TrackModel | null = null;
  private environment: EnvironmentHandle | null = null;
  private speedEffects: SpeedEffects | null = null;
  private showcaseRoot: THREE.Group | null = null;
  private elapsed = 0;
  private cameraPosition = new THREE.Vector3();
  private cameraLookAt = new THREE.Vector3();
  private previousGrounded = true;
  private previousBoost = 0;
  private landingKick = 0;
  private readonly forward = new THREE.Vector3();
  private readonly right = new THREE.Vector3();
  private readonly showcaseDesiredPosition = new THREE.Vector3();
  private readonly chaseCarPosition = new THREE.Vector3();
  private readonly chaseDesiredPosition = new THREE.Vector3();
  private readonly chaseVelocityDirection = new THREE.Vector3();
  private readonly chaseLookDirection = new THREE.Vector3();
  private readonly chaseDesiredLookAt = new THREE.Vector3();

  constructor(private readonly bridge: GameRenderBridge) {
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.08;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.element = this.renderer.domElement;
    this.element.className = 'game-canvas';
    this.element.setAttribute('aria-label', 'Slipstream Forge 3D game view');
    this.element.tabIndex = -1;
  }

  mount(container: HTMLElement): void {
    if (this.mounted) return;
    this.mounted = true;
    container.appendChild(this.element);
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(container);
    this.resize();
    this.timer.connect(document);
    this.timer.reset();
    this.scheduleNextFrame();
  }

  dispose(): void {
    if (!this.mounted) return;
    this.mounted = false;
    if (this.frameUsesAnimationFrame) cancelAnimationFrame(this.frame);
    else window.clearTimeout(this.frame);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.timer.dispose();
    this.clearScene();
    this.renderer.dispose();
    this.element.remove();
  }

  private readonly loop = (): void => {
    if (!this.mounted) return;
    this.scheduleNextFrame();
    this.timer.update();
    const delta = Math.min(this.timer.getDelta(), 0.05);
    this.elapsed += delta;
    this.bridge.tick(delta);
    const snapshot = this.bridge.getSnapshot();
    this.ensureScene(snapshot);
    if (this.sceneMode === 'race') this.updateRace(delta, snapshot);
    else this.updateShowcase(delta, snapshot);
    this.renderer.render(this.scene, this.camera);
  };

  private scheduleNextFrame(): void {
    if (this.bridge.getSnapshot().settings.vsync) {
      this.frameUsesAnimationFrame = true;
      this.frame = requestAnimationFrame(this.loop);
    } else {
      // An explicit high-rate desktop loop is the closest portable WebGL
      // equivalent to an unlocked swap interval; the compositor may still
      // present the newest frame at the display's refresh cadence.
      this.frameUsesAnimationFrame = false;
      this.frame = window.setTimeout(this.loop, 1000 / 120);
    }
  }

  private resize(): void {
    const parent = this.element.parentElement;
    if (!parent) return;
    const width = Math.max(1, parent.clientWidth);
    const height = Math.max(1, parent.clientHeight);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  private ensureScene(snapshot: GameSnapshot): void {
    const shouldRace = snapshot.screen === 'race' || snapshot.screen === 'results' || snapshot.settingsFromPause;
    const mode: SceneMode = shouldRace ? 'race' : 'showcase';
    const key = mode === 'race'
      ? `${mode}:${snapshot.selectedTrackId}:${snapshot.selectedCarId}:${snapshot.settings.sceneryDensity}`
      : `${mode}:${snapshot.selectedCarId}:${snapshot.selectedTrackId}`;
    if (this.sceneMode === mode && this.sceneKey === key) return;
    this.clearScene();
    this.sceneMode = mode;
    this.sceneKey = key;
    if (mode === 'race') this.buildRace(snapshot);
    else this.buildShowcase(snapshot);
  }

  private clearScene(): void {
    const activeRenderTarget = this.renderer.getRenderTarget();
    if (activeRenderTarget) {
      this.renderer.setRenderTarget(null);
      activeRenderTarget.dispose();
    }
    // Light.dispose() also disposes the WebGL render targets owned by its
    // shadow map (and the secondary point-light shadow pass, when present).
    this.scene.traverse((object) => {
      if (object instanceof THREE.Light) object.dispose();
    });
    this.track?.dispose();
    this.environment?.dispose();
    this.speedEffects?.dispose();
    if (this.car) disposeCarModel(this.car);
    this.track = null;
    this.environment = null;
    this.speedEffects = null;
    this.car = null;
    this.showcaseRoot = null;
    for (const child of [...this.scene.children]) {
      child.removeFromParent();
      child.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) return;
        object.geometry.dispose();
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        materials.forEach((material) => material.dispose());
      });
    }
    this.renderer.renderLists.dispose();
    this.scene.fog = null;
    this.previousGrounded = true;
    this.previousBoost = 0;
  }

  private buildShowcase(snapshot: GameSnapshot): void {
    const carDefinition = getCarById(snapshot.selectedCarId);
    const track = getTrackById(snapshot.selectedTrackId);
    this.scene.background = new THREE.Color(0x070c16);
    this.scene.fog = new THREE.FogExp2(0x070c16, 0.018);
    const root = new THREE.Group();
    this.showcaseRoot = root;
    this.scene.add(root);

    const hemisphere = new THREE.HemisphereLight(0xaacfff, 0x060910, 1.65);
    const key = new THREE.SpotLight(0xffffff, 55, 50, Math.PI / 5, 0.58, 1.4);
    key.position.set(8, 13, 9);
    key.castShadow = true;
    const rim = new THREE.SpotLight(track.palette.accent, 95, 40, Math.PI / 4, 0.45, 1.2);
    rim.position.set(-10, 7, -5);
    const fill = new THREE.PointLight(track.palette.secondary, 28, 25, 1.6);
    fill.position.set(7, 2, -4);
    root.add(hemisphere, key, rim, fill);

    const stageMaterial = new THREE.MeshStandardMaterial({ color: 0x111925, roughness: 0.26, metalness: 0.68 });
    const stage = new THREE.Mesh(new THREE.CylinderGeometry(5.7, 6.3, 0.72, 48), stageMaterial);
    stage.position.y = -0.42;
    stage.receiveShadow = true;
    root.add(stage);
    for (let ringIndex = 0; ringIndex < 3; ringIndex += 1) {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(6.4 + ringIndex * 1.8, 0.07 + ringIndex * 0.018, 8, 80),
        new THREE.MeshStandardMaterial({
          color: ringIndex % 2 === 0 ? track.palette.accent : track.palette.secondary,
          emissive: ringIndex % 2 === 0 ? track.palette.accent : track.palette.secondary,
          emissiveIntensity: 3,
        }),
      );
      ring.rotation.x = Math.PI / 2;
      ring.position.y = -0.02 - ringIndex * 0.07;
      root.add(ring);
    }

    const backdropRoad = new THREE.Mesh(
      new THREE.RingGeometry(15, 25, 72, 2, -0.35, Math.PI * 1.35),
      new THREE.MeshStandardMaterial({ color: track.palette.road, roughness: 0.75, metalness: 0.2, side: THREE.DoubleSide }),
    );
    backdropRoad.rotation.x = -Math.PI / 2;
    backdropRoad.position.set(4, -2.8, -8);
    root.add(backdropRoad);

    for (let index = 0; index < 18; index += 1) {
      const height = 1.5 + (index % 5) * 1.8;
      const pylon = new THREE.Mesh(
        new THREE.BoxGeometry(0.12, height, 0.12),
        new THREE.MeshStandardMaterial({
          color: index % 2 ? track.palette.accent : track.palette.secondary,
          emissive: index % 2 ? track.palette.accent : track.palette.secondary,
          emissiveIntensity: 2.4,
        }),
      );
      const angle = (index / 18) * Math.PI * 2;
      pylon.position.set(Math.cos(angle) * 12, height / 2 - 1.2, Math.sin(angle) * 12);
      pylon.userData.phase = angle;
      root.add(pylon);
    }

    this.car = createCarModel(carDefinition, true);
    this.car.root.scale.setScalar(1.28);
    this.car.root.position.y = 0;
    root.add(this.car.root);
    this.camera.position.set(9.7, 4.4, 12.2);
    this.camera.lookAt(0, 1.2, 0);
    this.camera.fov = 54;
    this.camera.updateProjectionMatrix();
  }

  private updateShowcase(delta: number, snapshot: GameSnapshot): void {
    if (!this.showcaseRoot || !this.car) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const orbit = reduced ? 0.25 : this.elapsed * 0.075;
    const screenBias = snapshot.screen === 'car-select' ? 0.05 : snapshot.screen === 'track-select' ? -0.08 : 0;
    this.car.root.rotation.y = Math.sin(this.elapsed * 0.22) * 0.14 + orbit * 0.18 + screenBias;
    this.car.setWheelMotion(2.5, Math.sin(this.elapsed * 0.4) * 0.25, delta);
    this.showcaseDesiredPosition.set(
      Math.sin(orbit) * 2 + 9.7,
      4.4 + Math.sin(this.elapsed * 0.3) * 0.12,
      Math.cos(orbit) * 2 + 12.2,
    );
    this.camera.position.lerp(this.showcaseDesiredPosition, 1 - Math.exp(-delta * 1.5));
    this.camera.lookAt(0, 1.15, 0);
    this.showcaseRoot.children.forEach((child) => {
      if (typeof child.userData.phase === 'number') {
        child.scale.y = 0.86 + Math.sin(this.elapsed * 1.5 + child.userData.phase) * 0.14;
      }
    });
  }

  private buildRace(snapshot: GameSnapshot): void {
    const runtime = this.bridge.getTrackRuntime();
    if (!runtime) {
      this.buildShowcase(snapshot);
      this.sceneMode = 'showcase';
      return;
    }
    const trackDefinition = getTrackById(snapshot.selectedTrackId);
    const carDefinition = getCarById(snapshot.selectedCarId);
    const lighting = configureTrackLighting(this.scene, trackDefinition);
    this.scene.add(lighting);
    this.track = createTrackModel(runtime);
    this.scene.add(this.track.root);
    this.environment = createEnvironment(trackDefinition, snapshot.settings.sceneryDensity, this.track.roadPoints);
    this.scene.add(this.environment.root);
    this.car = createCarModel(carDefinition);
    this.scene.add(this.car.root);
    this.speedEffects = new SpeedEffects(trackDefinition.palette.accent);
    this.scene.add(this.speedEffects.root);

    const start = runtime.getStartTransform();
    this.cameraPosition.copy(start.position).addScaledVector(start.forward, -10).addScaledVector(start.up, 4.8);
    this.cameraLookAt.copy(start.position).addScaledVector(start.up, 1.2).addScaledVector(start.forward, 8);
    this.camera.position.copy(this.cameraPosition);
    this.camera.lookAt(this.cameraLookAt);
    this.camera.fov = 62;
    this.camera.updateProjectionMatrix();
  }

  private updateRace(delta: number, snapshot: GameSnapshot): void {
    const state = this.bridge.getVehicleState();
    if (!state || !this.car) return;
    const telemetry = snapshot.race?.telemetry;
    this.applyVehicleTransform(state);
    const input = this.bridge.getLastInput();
    const speed = telemetry?.speedMps ?? Math.hypot(state.velocity.x, state.velocity.y, state.velocity.z);
    this.car.setWheelMotion(state.progress >= 0 ? speed : -speed, input.steer, delta);
    this.car.setBraking(input.brake > 0.1);
    this.track?.update(this.elapsed);
    this.environment?.update(this.elapsed, speed);

    if (telemetry && this.speedEffects) {
      this.forward.set(Math.sin(state.yaw), 0, Math.cos(state.yaw)).normalize();
      this.right.set(this.forward.z, 0, -this.forward.x).normalize();
      if (telemetry.boostAmount > 0.3 && this.previousBoost <= 0.3) this.speedEffects.triggerBoost();
      this.speedEffects.update(delta, this.elapsed, this.car.root.position, this.forward, this.right, speed, telemetry.sliding, telemetry.grounded);
      this.previousBoost = telemetry.boostAmount;
      if (!this.previousGrounded && telemetry.grounded) this.landingKick = Math.min(1, Math.abs(state.verticalVelocity) / 14 + 0.22);
      this.previousGrounded = telemetry.grounded;
    }
    this.updateChaseCamera(delta, state, speed);
  }

  private applyVehicleTransform(state: VehicleState): void {
    if (!this.car) return;
    this.car.root.position.set(state.position.x, state.position.y + 0.08, state.position.z);
    this.car.root.rotation.order = 'YXZ';
    this.car.root.rotation.set(state.pitch, state.yaw, -state.roll);
  }

  private updateChaseCamera(delta: number, state: VehicleState, speed: number): void {
    this.forward.set(Math.sin(state.yaw), 0, Math.cos(state.yaw)).normalize();
    this.right.set(this.forward.z, 0, -this.forward.x).normalize();
    this.chaseCarPosition.set(state.position.x, state.position.y, state.position.z);
    const speedFactor = THREE.MathUtils.smoothstep(speed, 8, 62);
    const distance = 8.7 + speedFactor * 3.3;
    const height = 3.8 + speedFactor * 1.15;
    this.chaseDesiredPosition.copy(this.chaseCarPosition).addScaledVector(this.forward, -distance);
    this.chaseDesiredPosition.y += height + this.landingKick * 0.26;
    this.chaseVelocityDirection.set(state.velocity.x, 0, state.velocity.z);
    if (this.chaseVelocityDirection.lengthSq() > 4) this.chaseVelocityDirection.normalize();
    else this.chaseVelocityDirection.copy(this.forward);
    this.chaseLookDirection.copy(this.forward).lerp(this.chaseVelocityDirection, 0.23).normalize();
    this.chaseDesiredLookAt.copy(this.chaseCarPosition);
    this.chaseDesiredLookAt.y += 1.15;
    this.chaseDesiredLookAt.addScaledVector(this.chaseLookDirection, 7 + speedFactor * 7);
    const positionLerp = 1 - Math.exp(-delta * (state.grounded ? 6.2 : 3.8));
    const lookLerp = 1 - Math.exp(-delta * 8.5);
    this.cameraPosition.lerp(this.chaseDesiredPosition, positionLerp);
    this.cameraLookAt.lerp(this.chaseDesiredLookAt, lookLerp);
    this.camera.position.copy(this.cameraPosition);
    this.camera.lookAt(this.cameraLookAt);
    this.landingKick = Math.max(0, this.landingKick - delta * 3.2);
    const desiredFov = 62 + speedFactor * 18 + Math.min(4, (this.bridge.getSnapshot().race?.telemetry.boostAmount ?? 0) * 4);
    this.camera.fov = THREE.MathUtils.lerp(this.camera.fov, desiredFov, 1 - Math.exp(-delta * 3.5));
    this.camera.updateProjectionMatrix();
  }
}
